import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { isErrorOrValidation, isErrorResponse } from '@app/core/components/grid/helpers/grid.helpers';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { AccountingService, LabourerItem } from '@app/services/accounting.service';
import { StockBatchItem, TypeaheadService } from '@app/services/typeahead.service';
import { Subject, takeUntil, mergeMap, switchMap, groupBy, debounceTime, catchError, EMPTY, from } from 'rxjs';

@GridModalComponent('orderpicknew')
@Component({
    selector: 'tt-order-pick-new',
    templateUrl: './order-pick-new.component.html',
    styleUrls: ['./order-pick-new.component.css'],
})
export class OrderPickNewComponent implements OnChanges, OnDestroy {
    @Input()
    public params?: OrderPickNewParams;

    public get?: OrderPickNewGet;

    @Output()
    public getLoaded = new EventEmitter<OrderPickNewGet>();

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public labourers: LabourerItem[] = [];

    public productUnits: ProductUnitItem[] = [];

    public orderlines: OrderlinePickToItem[] = [];

    public stockBatchesGrid?: GridOptions;

    public pickedBeforeGrid?: GridOptions;

    public saving = false;

    public distributing = false;

    public change$ = new Subject<UpdateOrderPickNewParams>();

    public destroy$ = new Subject<void>();

    constructor(
        private datatask: DataTaskService,
        private accounting: AccountingService,
        private typeahead: TypeaheadService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) stateContext: StateDialogContext,
    ) {
        this.initFromDialog(stateContext);

        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.updateOrderPickNew(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onUpdateOrderPickNewFromResponse });
    }

    private onUpdateOrderPickNewFromResponse = (response: Partial<OrderPickNewGet>) => {
        if (!this.get) return;
        console.log('response :>> ', response);
        for (let k in response) {
            let key = k as keyof OrderPickNewGet;

            this.get[key] = response[key] as any;
        }

        if ('' + this.get.stock_keyno > '0') this.readStockBatchesGrid();

        this.stockBatchesGrid?.gridfunc?.refreshToolbarBtnDisability();
    };

    public searchStockBatches = () => {
        return this.typeahead.searchStockBatches({ stockbatch_search: this.get?.stockbatch_search ?? '', stockplace_id: this.get?.stockplace_id ?? '' });
    };

    public searchProducts = () => {
        return this.typeahead.lookUpProducts({ prod_name: this.get?.prod_name ?? '' });
    };

    public onStockBatchSelected(event?: StockBatchItem) {
        if (!this.get) return;

        this.get.stock_keyno = event?.stock_keyno ?? '0';
        this.get.stockbatch_keyno = event?.item_id ?? '0';
        this.get.stockbatch_search = event?.item_name ?? '';
        this.get.prod_id = event?.prod_id ?? '';
        this.get.prod_name = event?.prod_name ?? '';

        this.onChange('stockbatch_keyno');
        this.loadProductUnits();
        this.readStockBatchesGrid();
        this.readPickedBeforeGrid();
    }

    public onChange(key: keyof OrderPickNewGet) {
        if (!this.get) return;
        this.change$.next({ colname_changed: key, ...this.get });
    }

    public async onProductSelected(event?: Item) {
        if (!this.get) return;

        this.get.prod_id = !!event ? '' + event.item_id : '';
        this.get.prod_name = event?.item_name ?? '';

        this.loadProductUnits();

        const { input_unit_no, stock_keyno } = await this.updateProdIdOrderPickNew(this.get);

        this.get.input_unit_no = input_unit_no;
        this.get.stock_keyno = stock_keyno;

        this.onChange('stock_keyno');

        this.updateQuantityStockUnits();
        this.readStockBatchesGrid();
        this.readPickedBeforeGrid();
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;
            const records = this.stockBatchesGrid?.gridfunc?.getAllRows().map((row) => ({ stockbatch_keyno: row['stockbatch_keyno'], quantity_pick_now: row['quantity_pick_now'] }));
            const { errorcode, errormessage } = await this.saveOrderPickNew({ ...this.get, records });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async distribute() {
        if (!this.get) return;

        try {
            this.distributing = true;

            const records = this.stockBatchesGrid?.gridfunc?.getAllRows() ?? [];

            const response = await this.distributeOrderPickNew({ ...this.get, records: records.slice().map((row) => ({ stockbatch_keyno: row['stockbatch_keyno'], quantity_pick_now: row['quantity_pick_now'] })) });

            const lookup: { [key: string]: string | number } = {};

            for (let row of response) {
                lookup[row.stockbatch_keyno] = row.quantity_picked_now;
            }

            for (let row of records) {
                row['quantity_pick_now'] = lookup[row['stockbatch_keyno']];
            }

            this.stockBatchesGrid?.gridfunc?.refresh();
        } catch (error) {
            console.error(error);
        } finally {
            this.distributing = false;
        }
    }

    private async loadLabourers() {
        if (!!this.get) {
            this.labourers = await this.accounting.getLabourers({ businessco_no: this.get.businessco_no });
        }
    }

    private async loadProductUnits() {
        if (!!this.get) {
            this.productUnits = await this.getProductUnits({ prod_id: this.get.prod_id });
        }
    }

    private async loadOrderlines() {
        if (this.get) {
            this.orderlines = await this.getOrderlinePickTos({ order_internal_no: this.get.order_internal_no });
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getOrderPickNew(this.params);

            if (this.get.stock_keyno > '0') this.readStockBatchesGrid();
            this.readPickedBeforeGrid();
        }
    }

    public async updateQuantityStockUnits() {
        if (!this.get) return;

        this.get.input_quantity_stock_units = (await this.recalculateQuantityStockUnitsOrderPickNew(this.get)).input_quantity_stock_units;
        this.onChange('input_quantity_stock_units');
    }

    public readStockBatchesGrid() {
        if (!this.stockBatchesGrid) {
            this.stockBatchesGrid = new Grid()
                .setRememberId('w_orderpick_stockbatch_grid')
                .setLoadDataFunction({ method: 3702, parameters: () => this.get })
                .addRowButton({
                    name: 'materialitempick',
                    icon: 'fas fa-hand-point-right',
                    btn_type: 'primary',
                    tooltip: 'materialitempick',
                    translateTooltip: true,
                    disabled: () => ('' + this.get?.show_button_save === '1' ? 'hidden' : false),
                    func: async (row) => {
                        const argtype = this.get!.orderline_keyno_pick_to > '0' ? 'orderline_keyno' : 'order_internal_no';
                        const argvalue = this.get!.orderline_keyno_pick_to > '0' ? this.get!.orderline_keyno_pick_to : this.get!.order_internal_no;
                        const response: ErrorMessageResponse = await this.modal.openStateDialog({ state: 'materialitempick', params: `${row['stockbatch_keyno']}/${argtype}/${argvalue}` });

                        if (!!response) {
                            await this.loadGet();
                            this.stockBatchesGrid?.gridfunc?.read();
                        }
                    },
                });
        } else {
            this.stockBatchesGrid.gridfunc?.read();
        }
    }

    public readPickedBeforeGrid() {
        if (!this.pickedBeforeGrid) {
            this.pickedBeforeGrid = new Grid()
                .setRememberId('w_orderpick_pickedbefore_grid')
                .setLoadDataFunction({ method: 3707, parameters: () => this.get })
                .addToolbarButton({
                    name: 'orderpick_pickedbefore_delete_selected',
                    text: 'orderpick_pickedbefore_delete_selected',
                    icon: 'fas fa-trash',
                    cssClass: 'tt-button tt-button--danger im-grid-btn-xs-l',
                    type: 'datatask',
                    func: async () => {
                        const rows = this.pickedBeforeGrid!.gridfunc?.getIsSelectedRows();

                        if (!rows || rows.length === 0) return;
                        let count = 0;
                        let modalClosed = false;
                        const progress = this.modal.openProgressDialog({ value: count, max: rows.length, rememberId: 'w_orderpick_pickedbefore_grid.progress' });

                        progress.dialogRef.beforeClosed().subscribe({ next: () => (modalClosed = true) });

                        for (let row of rows) {
                            try {
                                if (modalClosed) break;
                                let response = (await this.datatask.Post(3708, { ...row }))[0];

                                if (isErrorOrValidation(response) || modalClosed) {
                                    if (isErrorResponse(response)) {
                                        this.modal.show({ title: 'error_occured', translateTitle: true, message: response[0]?.errormessage ?? '', type: 'warning' });
                                    }

                                    progress.updateStatus('invalid');
                                    break;
                                } else {
                                    count++;
                                    progress.updateValue(count);
                                }

                                if (count === rows.length) {
                                    progress.updateStatus('finish');
                                }
                            } catch (error) {
                                console.error(error);
                                break;
                            } finally {
                                await this.loadGet();
                                this.readStockBatchesGrid();
                                this.readPickedBeforeGrid();
                            }
                        }
                    },
                });
        } else {
            this.pickedBeforeGrid.gridfunc?.read();
        }
    }

    /**
     * Retrieves the order pick new for the given params.
     *
     * @datatask 3700
     * @datataskName sp_ws_p2_w_orderpicknew_get
     * @param params params for the order pick new get.
     * @returns promise containing the order pick new item.
     */
    private async getOrderPickNew(params: OrderPickNewParams): Promise<OrderPickNewGet> {
        return (await this.datatask.Post(3700, params))[0];
    }

    /**
     *
     * @datatask 3969
     * @datataskName sp_ws_p2_w_orderpicknew_colchanged
     * @param params
     * @returns
     */
    private async updateOrderPickNew(params: UpdateOrderPickNewParams): Promise<Partial<OrderPickNewGet>> {
        return (await this.datatask.Post(3969, params))[0];
    }

    /**
     * Updates the new prod id for the order pick new.
     *
     * @datatask 3701
     * @datataskName sp_ws_p2_w_orderpicknew_prod_id_changed
     * @param params the order pick new that got prod id updated.
     * @returns promise containing new input unit no and stock keyno.
     */
    private async updateProdIdOrderPickNew(params: OrderPickNewGet): Promise<{ input_unit_no: string | number; stock_keyno: string }> {
        return (await this.datatask.Post(3701, params))[0];
    }

    /**
     * Recalculates the quantity stock units for the given order pick new item.
     *
     * @datatask 3703
     * @datataskName sp_ws_p2_w_orderpicknew_recalc_quantity_stock_units
     * @param params the order pick new to reculculate quantity stock units for.
     * @returns promise contianing the new input_quantity_stock_units value.
     */
    private async recalculateQuantityStockUnitsOrderPickNew(params: OrderPickNewGet): Promise<{ input_quantity_stock_units: string | number }> {
        return (await this.datatask.Post(3703, params))[0];
    }

    /**
     * Distributes the given order pick new.
     *
     * @datatask 3705
     * @datataskName sp_ws_p2_w_orderpicknew_distribute
     * @param params the order pick to to distribute.
     * @returns promise containing
     */
    private async distributeOrderPickNew(params: DistributeOrderPickNewParams): Promise<{ stockbatch_keyno: string; quantity_picked_now: string | number }[]> {
        return this.datatask.Post(3705, params);
    }

    private async getOrderlinePickTos(params: { order_internal_no: string }): Promise<OrderlinePickToItem[]> {
        return this.datatask.Post(3925, params);
    }

    /**
     * Saves the given order pick item.
     *
     * @datatask 3704
     * @datataskName sp_ws_p2_w_orderpicknew_save
     * @param params the order pick new get to save.
     * @returns promise containing error message response.
     */
    private async saveOrderPickNew(params: SaveOrderPickNewParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3704, params))[0];
    }

    /**
     * TODO: move to appropriate service.
     * Retrieves list of product units.
     *
     * @datatask 599
     * @datataskName sp_ws_p2_productunit_list_select
     * @params params
     */
    private async getProductUnits(params: GetProductsUnitsParams): Promise<ProductUnitItem[]> {
        return this.datatask.Post(599, params);
    }

    private async init() {
        await this.loadGet();
        this.loadLabourers();
        this.loadProductUnits();
        this.loadOrderlines();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.change$.complete();
    }

    private async initFromDialog(state?: StateDialogContext) {
        if (!!state) {
            this.params = state.params as unknown as OrderPickNewParams;
            state.setSize({ width: 'min(230rem, 85vw)', height: '95vh' });
            await this.init();
            this.submit.subscribe({ next: (value: ErrorMessageResponse) => state.dialogRef.close(value) });
            state.setHeading(this.get?.webpage_heading ?? '');
        }
    }
}

export interface DistributeOrderPickNewParams extends OrderPickNewGet {
    records: { stockbatch_keyno: string; quantity_pick_now: string | number }[];
}
export interface OrderPickNewParams {
    argtype: string;
    argvalue: string;
}

export interface OrderPickNewGet {
    order_internal_no: string;
    order_order_no: string;
    prod_id: string;
    prod_name: string;
    argtype: string;
    argvalue: string;
    stock_keyno: string;
    stockbatch_keyno: string;
    orderline_keyno_pick_to: string;
    /** decimals 2 */
    input_quantity: string | number;

    /** decimals 0 */
    input_unit_no: string | number;
    /** decimals 2 */
    input_quantity_stock_units: string | number;
    note: string;
    stockbatch_search: string;
    labour_no: string;
    labour_name: string;
    show_labour_no: '1' | '0';
    businessco_no: string;
    stockplace_id: string;
    webpage_heading: string;
    show_button_save: '1' | '0';
    quantity_stock_need: string | number;
    quantity_stock_picked_before: string | number;
    quantity_stock_rest: string | number;
}

interface ProductUnitItem {
    unit_no: string;
    shortname: string;
    orderby: string;
}

interface GetProductsUnitsParams {
    prod_id: string;
}

interface OrderlinePickToItem {
    orderline_keyno: string;
    orderline_name: string;
}

interface SaveOrderPickNewParams extends OrderPickNewGet {
    records?: { stockbatch_keyno: string; quantity_pick_now: string | number }[];
}

export interface UpdateOrderPickNewParams extends OrderPickNewGet {
    colname_changed: keyof OrderPickNewGet;
}
